/**
 * Bridge between StorageAdapter and zustand's `persist` middleware.
 *
 * zustand wants a `StateStorage` of raw strings. Our adapters deal in JSON
 * values, so this wrapper stores the string zustand hands it under the same
 * namespaced key an adapter would use. Everything still flows through the
 * adapter, which is the point: swapping in a CloudAdapter later changes one
 * line in src/app/stores/index.ts and nothing else.
 */

import { createJSONStorage, type StateStorage } from 'zustand/middleware';
import { getDefaultAdapter } from './adapters';
import type { StorageAdapter } from './types';

/** zustand string storage backed by whichever adapter the app is running on. */
export function adapterStateStorage(adapter?: StorageAdapter): StateStorage {
  const pick = (): StorageAdapter => adapter ?? getDefaultAdapter();
  return {
    async getItem(name: string): Promise<string | null> {
      return pick().get<string>(name);
    },
    async setItem(name: string, value: string): Promise<void> {
      await pick().set<string>(name, value);
    },
    async removeItem(name: string): Promise<void> {
      await pick().remove(name);
    },
  };
}

/** Ready-made `storage` option for `persist(...)`. */
export function adapterStorage<T>(adapter?: StorageAdapter) {
  return createJSONStorage<T>(() => adapterStateStorage(adapter));
}
